
import Utils, { NodeMedia } from "./Utils";
import { NodeType, ContentNode, ContentNodeDates, SiteDataImage, SiteData, HtmlHeadMeta } from "~/libs/Types";

/**
  * SEO Meta Data Builder Lib
  */
export class MetaData {
    nodeSchemaTypes: Map<NodeType, () => void>;

    /**
     * Content Node
     */
    node: ContentNode;

    /**
     * Static meta data.
     */
    siteMeta: any;

    /**
     * Configured site URL.
     */
    siteUrl?: string;

    /**
     * Content context URL.
     */
    nodeUrl?: string;

    /**
     * Matomo Tag Management System URL
     */
    matomoTmsUrl?: string;

    /**
     * Page title, falling back to site title.
     */
    title?: string;

    /**
     * Page description using node, and falling back to site global config.
     */
    description?: string;

    /**
     * Website logo image.
     */
    logoImage: SiteDataImage = {
        path: "",
        width: "",
        height: ""
    };

    /**
     * Content Image.
     */
    pageImage: SiteDataImage = {
        path: "",
        width: "",
        height: ""
    };

    /**
     * Robots meta tag.
     */
    robots?: string;

    /**
     * Set content sub type.
     */
    contentType?: NodeType;

    /**
     * Breadcrumb array of menu links used to build breadcrumb schema.
     */
    breadcrumbs: string[] = [];

    /**
     * Formatted schema meta data items.
     */
    schemaItems: any[] = [];

    /**
     * Schema.org IDs.
     */
    schemaIds: any = {
        organization: null,
        logo: null,
        website: null,
        webpage: null,
        location: null
    };

    /**
     * Meta data Constructor.
     *
     * @param {SiteData}             siteMeta
     * @param {ContentNodeInterface} node
     */
    public constructor (siteMeta: SiteData, node: ContentNode) {
        // Populate Types
        this.nodeSchemaTypes = new Map();
        this.nodeSchemaTypes.set(NodeType.page, this.schemaArticle);
        this.nodeSchemaTypes.set(NodeType.blog, this.schemaBlogPosting);

        // Start populating with data
        this.node = node;
        this.siteMeta = siteMeta;
        this.siteUrl = siteMeta.siteUrl;
        this.robots = node.metaData?.robots || "index, follow";

        this.nodeUrl = this.makeUrl(node.fullPath ?? "");
        this.title = this.formatTitle();
        this.description = this.formatDescription();

        this.setType(node, NodeType.page);

        // Website Site logo
        if (siteMeta.siteLogo) {
            this.logoImage = siteMeta.siteLogo;
        }

        // Conditionally use node image them when available
        if (node.page?.image) {
            this.pageImage.path = Utils.nodeMediaUrl(node, NodeMedia.Image);
        }

        // If available use background
        if (this.pageImage.path === "" && node.page?.background) {
            this.pageImage.path = Utils.nodeMediaUrl(node, NodeMedia.Background);
        }

        // Fallback to logo if page image is emtpy
        if (this.pageImage.path === "" && this.logoImage) {
            this.pageImage = this.logoImage;
        }

        // Schema @id's
        this.schemaIds.organization = this.makeUrl("#organization");
        this.schemaIds.logo = this.makeUrl("#logo");
        this.schemaIds.website = this.makeUrl("#website");
        this.schemaIds.webpage = this.nodeUrl + "#webpage";
        this.schemaIds.location = this.makeUrl("#location");
    }

    // -------------------------------------------------------------------------

    public build (): HtmlHeadMeta {
        return {
            title: this.title,
            meta: this.meta().filter(item => !!item.content),
            link: this.links(),
            script: this.scripts()
        };
    }

    public setType (node: ContentNode, defaultType: NodeType): void {
        // Some pages like index pages are just web pages with no sub type
        if (node.metaData?.noSubSchema) {
            return;
        }

        // Check if we have a content type or need to use the fallback type
        if (!node.nodeType || this.contentType === NodeType.undefined) {
            this.contentType = defaultType;
            return;
        }

        this.contentType = node.nodeType;
    }

    public addBreadcrumbs (breadcrumbs: string[]): void {
        if (!!breadcrumbs && breadcrumbs.length > 0) {
            this.breadcrumbs = breadcrumbs;
        }
    }

    public setMatomoTmsUrl (url: string): void {
        this.matomoTmsUrl = url;
    }

    // -------------------------------------------------------------------------

    protected formatTitle (): string {
        if (this.node.metaData?.title) {
            return this.node.metaData.title;
        }

        if (this.node.title) {
            return this.node.title + " | " + this.siteMeta.name;
        }

        return this.siteMeta.title;
    }

    protected formatDescription (): string {
        // Prefer meta description
        if (this.node.metaData?.description) {
            return this.node.metaData.description;
        }

        // Fallback to headline if it exists
        if (this.node.page?.headline) {
            return this.node.page.headline;
        }

        // Use site global description as a last resort
        return this.siteMeta.description;
    }

    protected makeUrl (path: string): string {
        return `${this.siteUrl}${path}`;
    }

    protected links (): any[] {
        const links = [
            { rel: "canonical", href: this.nodeUrl }
        ];

        if (this.contentType === NodeType.blog && this.node.siblingLinks) {
            const { prev, next } = this.node.siblingLinks;
            if (prev) {
                links.push({ rel: "prev", href: this.makeUrl(prev.path) });
            }

            if (next) {
                links.push({ rel: "next", href: this.makeUrl(next.path) });
            }
        }

        return links;
    }

    protected meta (): any[] {
        return [
            // Brand our tools
            {
                key: "generator",
                name: "generator",
                content: this.siteMeta.name
            },

            // We do not support IE, but we try and force the best possible outcome
            {
                key: "ie-compatible",
                "http-equiv": "X-UA-Compatible",
                content: "IE=edge"
            },
            {
                key: "robots",
                name: "robots",
                content: this.robots
            },
            {
                key: "description",
                name: "description",
                content: this.description
            },

            // Dublin Core
            {
                key: "dc.title",
                name: "DC.Title",
                content: this.title
            },
            {
                key: "dc.language",
                name: "DC.Language",
                content: this.siteMeta.locale
            },
            {
                key: "dc.publisher",
                name: "DC.Publisher",
                content: this.siteMeta.name
            },
            {
                key: "dc.location",
                name: "DC.Location",
                content: this.siteMeta.country
            },

            // Open Graph (Facebook)
            {
                key: "og:site_name",
                property: "og:site_name",
                content: this.siteMeta.name
            },
            {
                key: "og:title",
                property: "og:title",
                content: this.title
            },
            {
                key: "og:type",
                property: "og:type",
                content: "article" // for now hard coded to generic item
            },
            {
                key: "og:url",
                property: "og:url",
                content: this.nodeUrl
            },
            {
                key: "og:locale",
                property: "og:locale",
                content: this.siteMeta.locale
            },
            {
                key: "og:description",
                property: "og:description",
                content: this.description
            },
            {
                key: "og:image",
                property: "og:image",
                content: this.makeUrl(this.pageImage.path)
            },
            {
                key: "og:image:width",
                property: "og:image:width",
                content: this.pageImage.width
            },
            {
                key: "og:image:height",
                property: "og:image:height",
                content: this.pageImage.height
            },
            {
                key: "twitter:card",
                name: "twitter:card",
                content: "summary" // 'summary_large_image'
            },

            // Twitter
            {
                key: "twitter:title",
                property: "twitter:title",
                content: this.title
            },
            {
                key: "twitter:description",
                property: "twitter:description",
                content: this.description
            },
            {
                key: "twitter:image",
                property: "twitter:image",
                content: this.makeUrl(this.pageImage.path)
            }
        ];
    }

    /**
     * "datePublished": "2020-05-15T14:46:21-05:00",
     * "dateModified": "2020-05-16T17:55:27-05:00",
     * "dateCreated": "2020-05-15T14:46:21-05:00",
     */
    protected addDates (data: any) {
        // First check if node has any dates
        if (!this.node.dates) {
            return data;
        }

        // Mini map of date fields
        const dateFields = {
            dateCreated: "created",
            datePublished: "published",
            dateModified: "modified"
        };

        // Used created date as the default if nothing else exists
        const nodeDates = this.node.dates as ContentNodeDates;
        const defaultDate = nodeDates.created ?? null;

        // Loop through map of fields
        for (const item of Object.entries(dateFields)) {
            const schemaField: string = item[0];
            const nodeField = item[1] as keyof ContentNodeDates;

            // If date field exists we use it directly
            if (nodeDates[nodeField]) {
                data[schemaField] = nodeDates[nodeField];
                continue;
            }

            // Fall back to default if available
            if (defaultDate) {
                data[schemaField] = defaultDate;
            }
        }

        return data;
    }

    protected getSubType (type: NodeType): () => void {
        // eslint-disable-next-line no-console
        const errorFallback = () => console.warn("No sub schema type determined");
        return this.nodeSchemaTypes.get(type) ?? errorFallback;
    }

    protected scripts () {
        const scripts: any[] = this.schemas();

        if (!this.matomoTmsUrl) {
            return scripts;
        }

        scripts.push({
            type: "text/javascript",
            innerHTHML: "var _mtm = window._mtm = window._mtm || []; _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'});"
        });

        scripts.push({
            type: "text/javascript",
            src: this.matomoTmsUrl,
            async: true
        });

        return scripts;
    }

    protected schemas () {
        // standard always present schemas
        this.schemaOrg();
        this.schemaWebsite();
        this.schemaWebPage();

        // Content type specific schemas
        if (this.contentType) {
            const typeSchemaMethod = this.getSubType(this.contentType);
            if (typeSchemaMethod) {
                typeSchemaMethod.call({});
            }
        }

        if (this.node.metaData.faq) {
            this.schemaFaq();
        }

        // Optional breadcrumb schemas
        if (this.breadcrumbs.length > 0) {
            this.schemaBreadcrumbs();
        }

        // Return formatted schemas
        return this.schemaItems.map(function (item) {
            return { type: "application/ld+json", json: item };
        });
    }

    protected schemaOrg () {
        const data = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": this.schemaIds.organization,
            name: this.siteMeta.name,
            alternateName: this.siteMeta.altName,
            url: this.siteUrl,
            logo: {
                "@type": "ImageObject",
                url: this.makeUrl(this.logoImage.path),
                width: this.logoImage.width,
                height: this.logoImage.height,
                "@id": this.schemaIds.logo
            },
            address: {
                "@type": "PostalAddress",
                addressCountry: this.siteMeta.country
            },
            areaServed: this.schemaRegions(),
            sameAs: "http://schema.org/Organization"
        };

        this.schemaItems.push(data);
    }

    protected schemaWebsite () {
        const data = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": this.schemaIds.website,
            url: this.siteUrl,
            inLanguage: this.siteMeta.locale,
            name: this.siteMeta.name,
            alternateName: this.siteMeta.altName,
            description: this.siteMeta.description,
            creator: {
                "@type": "Organization",
                "@id": this.schemaIds.organization
            },
            publisher: {
                "@type": "Organization",
                "@id": this.schemaIds.organization
            },
            sameAs: "http://schema.org/WebSite"
        };

        this.schemaItems.push(data);
    }

    protected schemaWebPage () {
        const data = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": this.schemaIds.webpage,
            name: this.title,
            url: this.nodeUrl,
            description: this.description,
            image: {
                "@type": "ImageObject",
                url: this.makeUrl(this.pageImage.path),
                width: this.pageImage.width,
                height: this.pageImage.height
            },
            publisher: {
                "@type": "Organization",
                "@id": this.schemaIds.organization
            },
            sameAs: "http://schema.org/WebPage"
        };

        this.schemaItems.push(this.addDates(data));
    }

    protected schemaArticle = () => {
        const data = {
            "@context": "https://schema.org",
            "@type": "Article",
            url: this.nodeUrl,
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": this.schemaIds.webpage
            },
            headline: this.node.page?.headline ?? this.siteMeta.slogan,
            isAccessibleForFree: "True",
            publisher: {
                "@type": "Organization",
                "@id": this.schemaIds.organization
            },
            author: {
                "@type": "Organization",
                "@id": this.schemaIds.organization
            },
            abstract: this.description,
            image: {
                "@type": "ImageObject",
                url: this.makeUrl(this.pageImage.path),
                width: this.pageImage.width,
                height: this.pageImage.height
            },
            audience: {
                "@type": "Audience",
                name: this.siteMeta.audience,
                audienceType: this.siteMeta.audienceType,
                geographicArea: this.schemaRegions()
            },
            sameAs: "http://schema.org/Article"
        };

        this.schemaItems.push(this.addDates(data));
    };

    protected schemaBlogPosting = () => {
        const data = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            url: this.nodeUrl,
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": this.schemaIds.webpage
            },
            headline: this.node.page?.headline ?? this.siteMeta.slogan,
            isAccessibleForFree: "True",
            publisher: {
                "@type": "Organization",
                "@id": this.schemaIds.organization
            },
            author: {
                "@type": "Organization",
                "@id": this.schemaIds.organization
            },
            abstract: this.description,
            image: {
                "@type": "ImageObject",
                url: this.makeUrl(this.pageImage.path),
                width: this.pageImage.width,
                height: this.pageImage.height
            },
            audience: {
                "@type": "Audience",
                name: this.siteMeta.audience,
                audienceType: this.siteMeta.audienceType,
                geographicArea: this.schemaRegions()
            },
            sameAs: "https://schema.org/BlogPosting"
        };

        this.schemaItems.push(this.addDates(data));
    };

    protected schemaFaq = () => {
        const data = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: this.node.metaData.faq?.map((faq) => {
                return {
                    "@type": "Question",
                    name: faq.question,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: faq.answer
                    }
                };
            }),
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": this.schemaIds.webpage
            }
        };

        this.schemaItems.push(this.addDates(data));
    };

    protected schemaRegions () {
        return this.siteMeta.regions.map((name: string) => {
            return {
                "@type": "AdministrativeArea",
                name,
                address: {
                    "@type": "PostalAddress",
                    addressCountry: name
                }
            };
        });
    }

    /**
     * A sub schema as a property of WebPage
     */
    protected schemaBreadcrumbs () {
        const items = this.breadcrumbs.map((item: any, i) => {
            return {
                "@type": "ListItem",
                position: i + 1,
                item: {
                    name: item.title,
                    "@id": this.makeUrl(item.link),
                    url: this.makeUrl(item.link)
                }
            };
        });

        const data = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: items
        };

        this.schemaItems.push(data);
    }

    // -------------------------------------------------------------------------
}
